import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import PatientIntakeNavigation from '@brands/Components/PatientIntakeNavigation/PatientIntakeNavigation';
import { useWindowSize } from '@brands/hooks';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import useRoleNavigate from '@brands/hooks/useRoleNavigate';
import { useSnackbar } from '@brands/hooks/useStackbar';
import { CaseType } from '@brands/services/cases/types/ICase';
import { Pricings } from '@brands/services/identity/getPricings';
import { getCaseTypeFromForm, hasUploadedFile } from '@brands/Dashboard/PatientForms/PatientForm/utils/clinicalEnablementFlow';
import { selectOrganizationPrices } from '@brands/store/selectors/organizationPrices';
import { selectPatientForm } from '@brands/store/selectors/patientForm';
import { setForm } from '@brands/store/slices/patientFormSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import GenericSnackbar from '@brands/Utils/GenericSnackbar';
import { getSecondary } from '@brands/Utils/getPrimaryColor';
import { needsPayment } from '@brands/Utils/needsPayment';
import { painDurationOptions } from '@brands/Utils/selectButtonOptions';
import { painOptions } from '@brands/Utils/selectOptions';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Chip } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

import Container from '../../../../Components/Container/Container';
import TextField from '../../../../Components/Inputs/MuiTextField/TextField';
import styles from './styles.module.scss';

const emergencyChips = [
  { value: 'tooth_pain', label: 'Tooth Pain' },
  { value: 'abscess', label: 'Abscess' },
  { value: 'swollen_gums', label: 'Swollen Gums' },
  { value: 'bleeding_gums', label: 'Bleeding Gums' },
  { value: 'broken_tooth', label: 'Broken Tooth' },
  { value: 'chipped_tooth', label: 'Chipped Tooth' },
  { value: 'lost_filling', label: 'Lost Filling' },
  { value: 'tooth_trauma', label: 'Tooth Trauma' },
  { value: 'none_of_these', label: 'None Of These' },
];

const nonEmergencyChips = [
  { value: 'tooth_pain', label: 'Tooth Pain' },
  { value: 'mouth_injury', label: 'Mouth Injury' },
  { value: 'tongue_injury', label: 'Tongue Injury' },
  { value: 'cheek_injury', label: 'Cheek Injury' },
  { value: 'gum_injury', label: 'Gum Injury' },
  { value: 'loose_filling', label: 'Loose Filling' },
  { value: 'loose_crown', label: 'Loose Crown' },
  { value: 'tips_and_advice', label: 'Tips and Advice' },
  { value: 'none_of_these', label: 'None of These' },
];

export const getPainReasonForVisitText = (value: string, isEmergency?: boolean) => {
  const source = isEmergency ? emergencyChips : nonEmergencyChips;
  return source.find((item) => item.value === value)?.label || value;
};

type DescribePainLevelSubmit = {
  experiencingEmergency: boolean | null;
  painLevel: string;
  describePain: string[];
  daysInPain: string;
  reasonForVisit: string[];
  additionalInfo: string;
};

const validationSchema = Yup.object().shape({
  experiencingEmergency: Yup.boolean().required('Select an option in <Are you experiencing a dental emergency?>'),

  painLevel: Yup.string().when('experiencingEmergency', {
    is: false,
    then: Yup.string().required('Select an option in <How would you rate your pain level?>'),
    otherwise: Yup.string(), // don't require if emergency == true
  }),

  describePain: Yup.array().when(['painLevel'], {
    is: (painLevel: string) => painLevel !== 'noPain',
    then: Yup.array().min(1, 'Select at least one option in <How would you describe the pain?>').required(),
    otherwise: Yup.array().notRequired(),
  }),

  daysInPain: Yup.string().when(['painLevel'], {
    is: (painLevel: string) => painLevel !== 'noPain',
    then: Yup.string().required('Select an option in <How long have you been in pain?>'),
    otherwise: Yup.string().notRequired(),
  }),

  reasonForVisit: Yup.array().when('experiencingEmergency', {
    is: true,
    then: Yup.array().min(1, 'Select at least one option in <What symptoms are you experiencing?>').required(),
    otherwise: Yup.array().min(1, 'Select at least one option in <What is the reason for your visit?>').required(),
  }),

  additionalInfo: Yup.string(),
});

// Clinical Enablement drops every field on this screen except the free-text
// note — required unless a file was already attached in the earlier
// upload-image step, same rule as the medical-history questions.
const clinicalEnablementValidationSchema = (noteRequired: boolean) =>
  Yup.object().shape({
    experiencingEmergency: Yup.boolean().nullable(),
    painLevel: Yup.string(),
    describePain: Yup.array(),
    daysInPain: Yup.string(),
    reasonForVisit: Yup.array(),
    additionalInfo: noteRequired
      ? Yup.string().required('Tell us anything you would like the dentist to know, or attach a photo/file instead.')
      : Yup.string(),
  });

const DescribePainLevel = (): JSX.Element => {
  const screenSize = useWindowSize();
  const navigate = useNavigate();
  const roleNavigate = useRoleNavigate();
  const dispatch = useAppDispatch();

  const { values } = useAppSelector(selectPatientForm);
  const { currentOrganizationPrices } = useAppSelector(selectOrganizationPrices);
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const erSectionRef = useRef<HTMLDivElement | null>(null);

  const [loading, setLoading] = useState(false);
  const [expandERSection, setExpandERSection] = useState(false);
  // A ref, not state: the unmount save below runs with the closure of the last
  // committed render, so state cleared in onSubmit was still `true` there and
  // the submitted answers were written back as a draft, pre-filling this page
  // on the patient's next case.
  const hasUnsavedChangesRef = useRef(false);

  const caseType = getCaseTypeFromForm(values);
  const isClinicalEnablement = caseType === CaseType.clinical_enablement;
  const noteRequired = isClinicalEnablement && !hasUploadedFile(values);
  const currentOrganizationPrice = currentOrganizationPrices?.find((price: Pricings) =>
    price.case_types.includes(caseType as string),
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isValid },
    watch,
  } = useForm<DescribePainLevelSubmit>({
    mode: 'onChange',
    resolver: yupResolver(isClinicalEnablement ? clinicalEnablementValidationSchema(noteRequired) : validationSchema),
    defaultValues: {
      experiencingEmergency: null,
      painLevel: '',
      describePain: [],
      daysInPain: '',
      reasonForVisit: [],
      additionalInfo: '',
    },
  });

  const saveFormData = useCallback((): void => {
    if (hasUnsavedChangesRef.current) {
      const currentValues = {
        ...getValues(),
      };
      sessionStorage.setItem('concerns', JSON.stringify(currentValues));
    }
  }, [getValues]);

  const onSubmit = (data: DescribePainLevelSubmit): void => {
    try {
      setLoading(true);
      hasUnsavedChangesRef.current = false;
      const filteredPayload = values.filter((item) => item.form_question_id !== 16 && item.form_question_id !== 18);
      filteredPayload.push({
        form_question_id: 16,
        value: {
          experiencingEmergency: data.experiencingEmergency || false,
          painLevel: data.painLevel,
          describePain: data.painLevel === 'noPain' ? [] : data.describePain,
          daysInPain: data.painLevel === 'noPain' ? '' : data.daysInPain,
          reasonForVisit: data.reasonForVisit,
          additionalInfo: data.additionalInfo,
        },
      });
      filteredPayload.push({
        form_question_id: 18,
        value: {
          anythingElse: data.additionalInfo,
        },
      });
      dispatch(setForm(filteredPayload));
      sessionStorage.removeItem('concerns');
      if (isClinicalEnablement) {
        // Photos already happened before the medical-history steps for this
        // case type, so there is nothing left to collect before payment.
        roleNavigate(needsPayment(currentOrganizationPrice) ? '/payment-information' : '/confirm-payment');
      } else {
        roleNavigate('/upload-image');
      }
      setLoading(false);
    } catch (error) {
      hasUnsavedChangesRef.current = true;
      displayErrorDetails(error);
    }
  };

  const onInvalid = (e: any): void => {
    const error = Object.values(e)[0] as any;
    if (error) {
      showSnackbar(error.message, 'error');
    }
  };

  const isSelected = (value: string): boolean => watch('reasonForVisit')?.includes(value);

  const toggleChip = (value: string): void => {
    if (value === 'none_of_these') {
      if (isSelected(value)) {
        setValue('reasonForVisit', [], { shouldValidate: true });
      } else {
        setValue('reasonForVisit', ['none_of_these'], { shouldValidate: true });
      }
    } else {
      let updated = watch('reasonForVisit').filter((item) => item !== 'none_of_these');
      if (isSelected(value)) {
        updated = updated.filter((item) => item !== value);
      } else {
        updated = [...updated, value];
      }
      setValue('reasonForVisit', updated, { shouldValidate: true });
    }
  };

  useEffect(() => {
    if (expandERSection && erSectionRef.current) {
      erSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [expandERSection]);

  useEffect(() => {
    const selectedFormItem = values.find((item) => item.form_question_id === 16);
    const savedDraft = sessionStorage.getItem('concerns');
    const value = savedDraft ? JSON.parse(savedDraft) : selectedFormItem?.value;

    if (value) {
      setValue('experiencingEmergency', value.experiencingEmergency as boolean | null, { shouldValidate: true });
      setValue('painLevel', value.painLevel as 'noPain' | 'mild' | 'moderate' | 'severe', { shouldValidate: true });
      setValue('describePain', value.describePain as string[], { shouldValidate: true });
      setValue('daysInPain', value.daysInPain as string, { shouldValidate: true });
      setValue('reasonForVisit', value.reasonForVisit as string[], { shouldValidate: true });
      setValue('additionalInfo', value.additionalInfo as string, { shouldValidate: true });
    }
    hasUnsavedChangesRef.current = true;
  }, [setValue, values]);

  useEffect(() => {
    return () => {
      saveFormData();
    };
  }, [saveFormData]);

  useEffect(() => {
    // Handle page refresh/close
    const handleBeforeUnload = (): void => {
      saveFormData();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveFormData]);

  if (loading) {
    return <Loading fullScreen />;
  }
  return (
    <Container
      isVisibleOnMobile
      containerStyle={{
        justifyContent: screenSize.width >= 564 ? 'center' : 'start',
        overflow: 'hidden',
      }}
      childrenStyle={{
        zIndex: '2',
        paddingTop: screenSize.width > 768 ? '40px' : '16px',
        paddingBottom: screenSize.width > 768 ? '40px' : '16px',
      }}
      isSidenavVisible={false}
      isIntakeProcess
    >
      <div className={`fs-unmask ${styles.describePainLevelContainer}`}>
        <PatientIntakeNavigation progress={64} />
        <form className={`${styles.formContainer}`} onSubmit={handleSubmit(onSubmit, onInvalid)}>
          <div
            className={`${styles.describePainLevelWrapper} ${
              isClinicalEnablement ? styles.describePainLevelWrapperCE : ''
            }`}
          >
            <div>
              <div className={styles.describePainLevelTitle}>
                <span>Tell us about your dental concerns</span>
              </div>
            </div>
            <div className={styles.describePainLevelBody}>
              {!isClinicalEnablement && (
              <>
              <div className={styles.describePainLevelQuestion}>
                <div className={`fs-unmask ${styles.describePainLevelQuestionText}`}>
                  Are you experiencing a dental emergency? <span>*</span>
                </div>
                <div className={`fs-exclude ${styles.describePainLevelQuestionOptions}`}>
                  <Button
                    {...register('experiencingEmergency')}
                    variant={watch('experiencingEmergency') ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionEmergencyOption} fs-exclude  ${
                      watch('experiencingEmergency') ? styles.active : ''
                    }`}
                    onClick={() => {
                      setValue('experiencingEmergency', true, { shouldValidate: true });
                      setValue('reasonForVisit', [], { shouldValidate: true });
                    }}
                    style={{
                      backgroundColor: watch('experiencingEmergency') ? getSecondary() : 'transparent',
                      color: watch('experiencingEmergency') ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-experiencingEmergency-yes"
                  >
                    <div
                      className={styles.describePainLevelQuestionEmergencyContainer}
                      style={{ color: `${watch('experiencingEmergency') ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionEmergencyTitle}>
                        I’m having a dental emergency
                      </div>
                      <ul className={styles.describePainLevelQuestionEmergencyList}>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Moderate to severe tooth pain
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Abscess and/or swelling
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Bleeding or swollen gums
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Broken or chipped tooth or lost filling causing pain
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Trauma to the teeth (i.e. tooth knocked out)
                          </span>
                        </li>
                      </ul>
                    </div>
                  </Button>
                  <Button
                    variant={watch('experiencingEmergency') === false ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionEmergencyOption} fs-exclude  ${
                      watch('experiencingEmergency') === false ? styles.active : ''
                    }`}
                    onClick={() => {
                      setValue('experiencingEmergency', false, { shouldValidate: true });
                      setValue('reasonForVisit', [], { shouldValidate: true });
                    }}
                    style={{
                      backgroundColor: watch('experiencingEmergency') === false ? getSecondary() : 'transparent',
                      color: watch('experiencingEmergency') === false ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-experiencingEmergency-no"
                  >
                    <div
                      className={styles.describePainLevelQuestionEmergencyContainer}
                      style={{ color: `${watch('experiencingEmergency') === false ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionEmergencyTitle}>
                        My dental issue is not an emergency
                      </div>
                      <ul className={styles.describePainLevelQuestionEmergencyList}>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') === false ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Dull or mild tooth pain/sensitivity
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') === false ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Minor injury to the mouth, tongue, cheeks, or gums
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') === false ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Loose filling or crown
                          </span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') === false ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>Dry mouth</span>
                        </li>
                        <li className={styles.describePainLevelQuestionEmergencyListItem}>
                          <span
                            className={styles.describePainLevelQuestionEmergencyListItemIcon}
                            style={{ backgroundColor: `${watch('experiencingEmergency') === false ? '#fff' : ''}` }}
                          />
                          <span className={styles.describePainLevelQuestionEmergencyListItemText}>
                            Expert advice and tips on dental products
                          </span>
                        </li>
                      </ul>
                    </div>
                  </Button>
                </div>
              </div>
              <div className={styles.describePainLevelQuestion}>
                <div className={`fs-unmask ${styles.describePainLevelQuestionText}`}>
                  How would you rate your pain level? <span>*</span>
                </div>
                <div className={`fs-exclude ${styles.describePainLevelQuestionOptionsPainLevel}`}>
                  <Button
                    variant={watch('painLevel') === 'noPain' ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionPainLevelOption} fs-exclude  ${
                      watch('painLevel') === 'noPain' ? styles.active : ''
                    }`}
                    onClick={() => setValue('painLevel', 'noPain', { shouldValidate: true })}
                    style={{
                      backgroundColor: watch('painLevel') === 'noPain' ? getSecondary() : 'transparent',
                      color: watch('painLevel') === 'noPain' ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-painLevel-noPain"
                  >
                    <div
                      className={styles.describePainLevelQuestionPainLevelContainer}
                      style={{ color: `${watch('painLevel') === 'noPain' ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionPainLevelIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 20.8145C15.5228 20.8145 20 16.3374 20 10.8145C20 5.2915 15.5228 0.814453 10 0.814453C4.47717 0.814453 0 5.2915 0 10.8145C0 16.3374 4.47717 20.8145 10 20.8145ZM13.1085 6.6333C12.5312 6.61597 12.1139 6.85522 11.8497 7.09424C11.7197 7.21191 11.6255 7.33032 11.563 7.42041C11.5315 7.46582 11.5073 7.50488 11.49 7.53491L11.469 7.57275L11.4622 7.58569L11.4585 7.59302C11.458 7.59399 11.4578 7.59448 11.5697 7.6499L11.4576 7.59473C11.3357 7.84253 11.4377 8.14233 11.6854 8.26416C11.9313 8.38525 12.2281 8.28564 12.3519 8.04224L12.3557 8.0354C12.3607 8.02686 12.3702 8.01099 12.3845 7.99048C12.4135 7.94873 12.4589 7.8916 12.5208 7.83569C12.6405 7.72729 12.8169 7.625 13.079 7.63281C13.2927 7.63916 13.4539 7.70508 13.5732 7.78394C13.5935 7.79736 13.6125 7.81104 13.6305 7.82471C13.6456 7.83643 13.6599 7.84839 13.6735 7.86035C13.6965 7.88037 13.7173 7.90039 13.7357 7.91968C13.7681 7.95337 13.7932 7.98438 13.8113 8.00903C13.8254 8.02832 13.8347 8.04272 13.8395 8.05029L13.8425 8.05542C13.9735 8.29492 14.2731 8.3855 14.5151 8.25732C14.7593 8.12817 14.8523 7.82568 14.7231 7.58154L14.2812 7.81543C14.7231 7.58154 14.723 7.5813 14.7222 7.57983L14.7211 7.57764L14.7184 7.57275L14.7113 7.56006L14.6898 7.52417C14.6722 7.49561 14.6478 7.4585 14.6161 7.41553C14.5874 7.37671 14.5525 7.33276 14.5107 7.28564C14.4607 7.229 14.401 7.16797 14.3311 7.10693C14.0712 6.88013 13.6672 6.64966 13.1085 6.6333ZM5.49805 8.26392C5.25024 8.14209 5.14819 7.84229 5.27014 7.59448L5.71875 7.81543C5.27014 7.59448 5.27026 7.59424 5.27112 7.59277L5.27209 7.59058L5.27466 7.58545L5.28149 7.57251L5.30249 7.53467C5.31982 7.50464 5.34399 7.46558 5.37549 7.42017C5.43799 7.33008 5.53223 7.21167 5.66223 7.09399C5.92639 6.85498 6.34375 6.61597 6.92102 6.63306C7.47974 6.64941 7.88367 6.87988 8.14355 7.10669C8.27197 7.21875 8.36572 7.33008 8.42871 7.41553C8.46033 7.45825 8.48474 7.49536 8.50232 7.52393L8.5238 7.55981L8.53088 7.57251L8.53357 7.57739L8.53467 7.57959C8.53552 7.58105 8.53564 7.5813 8.09375 7.81543L8.53564 7.5813C8.59619 7.69556 8.60791 7.82251 8.57837 7.93872C8.54468 8.0708 8.45752 8.18848 8.32776 8.25732C8.08557 8.38525 7.78601 8.29492 7.65503 8.05518L7.65198 8.05005C7.64722 8.04248 7.63794 8.02808 7.62378 8.00903C7.61243 7.99341 7.59827 7.97534 7.5813 7.95557C7.55579 7.92627 7.52405 7.89331 7.48596 7.86011C7.36194 7.75171 7.17212 7.64087 6.89148 7.63257C6.62952 7.62476 6.45312 7.72705 6.33325 7.83545C6.2981 7.86719 6.26819 7.89941 6.24365 7.92896C6.2251 7.95142 6.20959 7.97217 6.19702 7.99023C6.18286 8.01074 6.17322 8.02661 6.16821 8.0354L6.16443 8.04199C6.12915 8.11157 6.07983 8.16919 6.02148 8.21338C5.87549 8.32422 5.67371 8.35034 5.49805 8.26392ZM16.7391 10.9534H3.2483C2.67282 10.9534 2.21541 11.4385 2.29257 12.0088C2.33835 12.3471 2.40111 12.7095 2.48467 13.002C3.49074 16.5233 6.68783 18.6113 9.99993 18.6113C13.312 18.6113 16.4151 16.5497 17.4743 13.0752C17.5689 12.7652 17.6408 12.3781 17.6935 12.0197C17.7779 11.4458 17.3192 10.9534 16.7391 10.9534Z"
                            fill={watch('painLevel') === 'noPain' ? '#fff' : '#49C6A4'}
                          />
                        </svg>
                      </div>
                      <div
                        className={styles.describePainLevelQuestionPainLevelTitle}
                        style={{ color: `${watch('painLevel') === 'noPain' ? '#fff' : ''}` }}
                      >
                        <div>No</div>
                        <div>Pain</div>
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant={watch('painLevel') === 'mildPain' ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionPainLevelOption} fs-exclude  ${
                      watch('painLevel') === 'mildPain' ? styles.active : ''
                    }`}
                    onClick={() => setValue('painLevel', 'mildPain', { shouldValidate: true })}
                    style={{
                      backgroundColor: watch('painLevel') === 'mildPain' ? getSecondary() : 'transparent',
                      color: watch('painLevel') === 'mildPain' ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-painLevel-mildPain"
                  >
                    <div
                      className={styles.describePainLevelQuestionPainLevelContainer}
                      style={{ color: `${watch('painLevel') === 'mildPain' ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionPainLevelIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            fill={watch('painLevel') === 'mildPain' ? '#fff' : '#8BC34A'}
                            d="M20 10.8145C20 16.3374 15.5228 20.8145 10 20.8145C4.47717 20.8145 0 16.3374 0 10.8145C0 5.2915 4.47717 0.814453 10 0.814453C15.5228 0.814453 20 5.2915 20 10.8145ZM14.6016 8.1123C14.6016 8.93213 13.937 9.59668 13.1172 9.59668C12.2974 9.59668 11.6328 8.93213 11.6328 8.1123C11.6328 7.29248 12.2974 6.62793 13.1172 6.62793C13.937 6.62793 14.6016 7.29248 14.6016 8.1123ZM6.88281 9.59668C7.70264 9.59668 8.36719 8.93213 8.36719 8.1123C8.36719 7.29248 7.70264 6.62793 6.88281 6.62793C6.06299 6.62793 5.39844 7.29248 5.39844 8.1123C5.39844 8.93213 6.06299 9.59668 6.88281 9.59668ZM14.5489 12.6086C14.7967 12.7306 14.8987 13.0303 14.7767 13.278L14.3281 13.0572C14.7767 13.278 14.7767 13.278 14.7767 13.278L14.7762 13.279L14.7756 13.2802L14.7741 13.2833L14.7695 13.2923C14.7658 13.2996 14.7608 13.3092 14.7545 13.3211C14.7418 13.3449 14.7238 13.3777 14.7004 13.4182C14.6536 13.4991 14.5848 13.6112 14.4928 13.7438C14.3093 14.0083 14.0306 14.3584 13.6468 14.7058C12.8749 15.4044 11.6724 16.0948 9.98517 16.0448C8.31657 15.9954 7.12752 15.3137 6.35996 14.6438C5.97775 14.3103 5.69951 13.9795 5.51551 13.7299C5.42334 13.605 5.35429 13.4996 5.30718 13.4235C5.28362 13.3854 5.2655 13.3546 5.25268 13.3322L5.23741 13.3049L5.23271 13.2963L5.23109 13.2933L5.23046 13.2921C5.23046 13.2921 5.22995 13.2912 5.67185 13.0572L5.22995 13.2912C5.10076 13.0471 5.19387 12.7445 5.43792 12.6153C5.68146 12.4864 5.98327 12.5788 6.11293 12.8217L6.11377 12.8233L6.12102 12.8362C6.12829 12.8489 6.14042 12.8696 6.15752 12.8973C6.19177 12.9526 6.24575 13.0353 6.32035 13.1365C6.46988 13.3392 6.70008 13.6134 7.01751 13.8904C7.64923 14.4418 8.62424 15.004 10.0148 15.0452C11.3867 15.0859 12.3482 14.5324 12.9757 13.9644C13.2915 13.6786 13.5213 13.3898 13.6712 13.1736C13.746 13.0659 13.8002 12.9773 13.8348 12.9175C13.852 12.8877 13.8643 12.8652 13.8717 12.8512L13.8792 12.8369M14.5489 12.6086C14.3013 12.4867 14.0013 12.5894 13.8792 12.8369L14.5489 12.6086Z"
                          />
                        </svg>
                      </div>
                      <div
                        className={styles.describePainLevelQuestionPainLevelTitle}
                        style={{ color: `${watch('painLevel') === 'mildPain' ? '#fff' : ''}` }}
                      >
                        <div>Mild</div>
                        <div>Pain</div>
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant={watch('painLevel') === 'moderatePain' ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionPainLevelOption} fs-exclude  ${
                      watch('painLevel') === 'moderatePain' ? styles.active : ''
                    }`}
                    onClick={() => setValue('painLevel', 'moderatePain', { shouldValidate: true })}
                    style={{
                      backgroundColor: watch('painLevel') === 'moderatePain' ? getSecondary() : 'transparent',
                      color: watch('painLevel') === 'moderatePain' ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-painLevel-moderatePain"
                  >
                    <div
                      className={styles.describePainLevelQuestionPainLevelContainer}
                      style={{ color: `${watch('painLevel') === 'moderatePain' ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionPainLevelIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 20.8145C15.5228 20.8145 20 16.3373 20 10.8145C20 5.29161 15.5228 0.814453 10 0.814453C4.47715 0.814453 0 5.29161 0 10.8145C0 16.3373 4.47715 20.8145 10 20.8145ZM8.36719 8.1123C8.36719 8.9321 7.70261 9.59668 6.88281 9.59668C6.06301 9.59668 5.39844 8.9321 5.39844 8.1123C5.39844 7.29251 6.06301 6.62793 6.88281 6.62793C7.70261 6.62793 8.36719 7.29251 8.36719 8.1123ZM13.1172 9.59668C13.937 9.59668 14.6016 8.9321 14.6016 8.1123C14.6016 7.29251 13.937 6.62793 13.1172 6.62793C12.2974 6.62793 11.6328 7.29251 11.6328 8.1123C11.6328 8.9321 12.2974 9.59668 13.1172 9.59668ZM7.25 12.7053C6.97386 12.7053 6.75 12.9292 6.75 13.2053C6.75 13.4815 6.97386 13.7053 7.25 13.7053H12.75C13.0261 13.7053 13.25 13.4815 13.25 13.2053C13.25 12.9292 13.0261 12.7053 12.75 12.7053H7.25Z"
                            fill={watch('painLevel') === 'moderatePain' ? '#fff' : '#DBD22C'}
                          />
                        </svg>
                      </div>
                      <div
                        className={styles.describePainLevelQuestionPainLevelTitle}
                        style={{ color: `${watch('painLevel') === 'moderatePain' ? '#fff' : ''}` }}
                      >
                        <div>Moderate</div>
                        <div>Pain</div>
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant={watch('painLevel') === 'severePain' ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionPainLevelOption} fs-exclude  ${
                      watch('painLevel') === 'severePain' ? styles.active : ''
                    }`}
                    onClick={() => setValue('painLevel', 'severePain', { shouldValidate: true })}
                    style={{
                      backgroundColor: watch('painLevel') === 'severePain' ? getSecondary() : 'transparent',
                      color: watch('painLevel') === 'severePain' ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-painLevel-severePain"
                  >
                    <div
                      className={styles.describePainLevelQuestionPainLevelContainer}
                      style={{ color: `${watch('painLevel') === 'severePain' ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionPainLevelIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 0.814453C15.5228 0.814453 20 5.29161 20 10.8145C20 16.3373 15.5228 20.8145 10 20.8145C4.47715 20.8145 0 16.3373 0 10.8145C0 5.29161 4.47715 0.814453 10 0.814453ZM10 12.5566C9.03036 12.5566 8.26837 12.8082 7.74512 13.0635C7.48385 13.191 7.28114 13.3196 7.1416 13.4189C7.07196 13.4685 7.01804 13.5114 6.97949 13.543C6.96019 13.5588 6.944 13.5721 6.93262 13.582L6.91895 13.5938L6.91406 13.5986L6.91211 13.5996L6.91113 13.6006C6.70837 13.7879 6.69561 14.1048 6.88281 14.3076C7.06949 14.5098 7.38397 14.5231 7.58691 14.3379L7.58984 14.335C7.59378 14.3315 7.6014 14.3253 7.6123 14.3164C7.63459 14.2982 7.67142 14.2692 7.72168 14.2334C7.82233 14.1618 7.97778 14.0623 8.18359 13.9619C8.59462 13.7614 9.20734 13.5566 10 13.5566C10.7927 13.5566 11.4054 13.7614 11.8164 13.9619C12.0222 14.0623 12.1777 14.1618 12.2783 14.2334C12.3286 14.2692 12.3654 14.2982 12.3877 14.3164C12.3986 14.3253 12.4062 14.3315 12.4102 14.335L12.4131 14.3379C12.616 14.5231 12.9305 14.5098 13.1172 14.3076C13.2809 14.1302 13.2918 13.8665 13.1562 13.6777L13.0889 13.6016L13.0879 13.5996L13.0859 13.5986L13.0811 13.5938L13.0674 13.582C13.056 13.5721 13.0398 13.5588 13.0205 13.543C12.982 13.5114 12.928 13.4685 12.8584 13.4189C12.7189 13.3196 12.5162 13.191 12.2549 13.0635C11.7316 12.8082 10.9696 12.5566 10 12.5566ZM6.88281 6.62793C6.06301 6.62793 5.39844 7.29251 5.39844 8.1123C5.39844 8.9321 6.06301 9.59668 6.88281 9.59668C7.70261 9.59668 8.36719 8.9321 8.36719 8.1123C8.36719 7.29251 7.70261 6.62793 6.88281 6.62793ZM13.1172 6.62793C12.2974 6.62793 11.6328 7.29251 11.6328 8.1123C11.6328 8.9321 12.2974 9.59668 13.1172 9.59668C13.937 9.59668 14.6016 8.9321 14.6016 8.1123C14.6016 7.29251 13.937 6.62793 13.1172 6.62793Z"
                            fill={watch('painLevel') === 'severePain' ? '#fff' : '#FF9800'}
                          />
                        </svg>
                      </div>
                      <div
                        className={styles.describePainLevelQuestionPainLevelTitle}
                        style={{ color: `${watch('painLevel') === 'severePain' ? '#fff' : ''}` }}
                      >
                        <div>Severe</div>
                        <div>Pain</div>
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant={watch('painLevel') === 'verySeverePain' ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionPainLevelOption} fs-exclude  ${
                      watch('painLevel') === 'verySeverePain' ? styles.active : ''
                    }`}
                    onClick={() => setValue('painLevel', 'verySeverePain', { shouldValidate: true })}
                    style={{
                      backgroundColor: watch('painLevel') === 'verySeverePain' ? getSecondary() : 'transparent',
                      color: watch('painLevel') === 'verySeverePain' ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-painLevel-verySeverePain"
                  >
                    <div
                      className={styles.describePainLevelQuestionPainLevelContainer}
                      style={{ color: `${watch('painLevel') === 'verySeverePain' ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionPainLevelIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 20.8145C15.5228 20.8145 20 16.3374 20 10.8145C20 5.2915 15.5228 0.814453 10 0.814453C4.47717 0.814453 0 5.2915 0 10.8145C0 16.3374 4.47717 20.8145 10 20.8145ZM6.88281 9.59668C7.70264 9.59668 8.36719 8.93213 8.36719 8.1123C8.36719 7.29248 7.70264 6.62793 6.88281 6.62793C6.06299 6.62793 5.39844 7.29248 5.39844 8.1123C5.39844 8.93213 6.06299 9.59668 6.88281 9.59668ZM13.1172 9.59668C13.937 9.59668 14.6016 8.93213 14.6016 8.1123C14.6016 7.29248 13.937 6.62793 13.1172 6.62793C12.2974 6.62793 11.6328 7.29248 11.6328 8.1123C11.6328 8.93213 12.2974 9.59668 13.1172 9.59668ZM5.90723 15.4761C5.97278 15.459 6.03467 15.4287 6.08875 15.3865C6.14551 15.3423 6.1936 15.2852 6.22803 15.2166L6.23376 15.2061C6.23999 15.1946 6.25085 15.1753 6.26624 15.1497C6.297 15.0981 6.34607 15.0205 6.41479 14.9255C6.55249 14.7354 6.76685 14.4783 7.0675 14.2205C7.6637 13.7097 8.60632 13.1895 10 13.1895C11.3937 13.1895 12.3363 13.7097 12.9325 14.2205C13.2332 14.4783 13.4475 14.7354 13.5852 14.9255C13.6539 15.0205 13.703 15.0981 13.7338 15.1497C13.7491 15.1753 13.7599 15.1946 13.7662 15.2061L13.7723 15.2175C13.8351 15.3423 13.9429 15.4292 14.066 15.4685C14.1095 15.4824 14.1549 15.4905 14.2009 15.4922C14.2822 15.4951 14.3654 15.478 14.4431 15.439C14.6899 15.3152 14.7894 15.0146 14.6655 14.7678L14.6628 14.7625L14.6583 14.7537L14.6438 14.7266L14.6218 14.6873L14.5924 14.637C14.548 14.5625 14.4828 14.4602 14.3953 14.3394C14.2205 14.0977 13.9543 13.7793 13.5831 13.4612C12.8356 12.8206 11.6688 12.1895 10 12.1895C8.33118 12.1895 7.16443 12.8206 6.41687 13.4612C6.04565 13.7793 5.77954 14.0977 5.60474 14.3394C5.57007 14.3872 5.53882 14.4324 5.51111 14.4739L5.48474 14.5137C5.45435 14.5603 5.42859 14.6018 5.40759 14.637C5.38538 14.6741 5.36829 14.7043 5.3562 14.7266L5.34167 14.7537L5.33716 14.7625L5.33557 14.7656C5.33496 14.7668 5.33435 14.7678 5.78125 14.9922L5.33435 14.7678C5.21045 15.0146 5.31006 15.3152 5.55688 15.439C5.61963 15.4705 5.68579 15.4875 5.75159 15.4915C5.80432 15.4946 5.85681 15.4893 5.90723 15.4761ZM13.8789 12.4082C14.2781 12.4082 14.6016 12.0847 14.6016 11.6855C14.6016 11.2864 14.0039 10.3845 13.8789 10.3845C13.7539 10.3845 13.1561 11.2864 13.1561 11.6855C13.1561 12.0847 13.4797 12.4082 13.8789 12.4082Z"
                            fill={watch('painLevel') === 'verySeverePain' ? '#fff' : '#FF5722'}
                          />
                        </svg>
                      </div>
                      <div
                        className={styles.describePainLevelQuestionPainLevelTitle}
                        style={{ color: `${watch('painLevel') === 'verySeverePain' ? '#fff' : ''}` }}
                      >
                        <div>Very Severe</div>
                        <div>Pain</div>
                      </div>
                    </div>
                  </Button>
                  <Button
                    variant={watch('painLevel') === 'worstPainPossible' ? 'contained' : 'outlined'}
                    className={`${styles.describePainLevelQuestionPainLevelOption} fs-exclude  ${
                      watch('painLevel') === 'worstPainPossible' ? styles.active : ''
                    }`}
                    onClick={() => setValue('painLevel', 'worstPainPossible', { shouldValidate: true })}
                    style={{
                      backgroundColor: watch('painLevel') === 'worstPainPossible' ? getSecondary() : 'transparent',
                      color: watch('painLevel') === 'worstPainPossible' ? '#fff' : '#000',
                    }}
                    data-testid="describe-pain-painLevel-worstPainPossible"
                  >
                    <div
                      className={styles.describePainLevelQuestionPainLevelContainer}
                      style={{ color: `${watch('painLevel') === 'worstPainPossible' ? '#fff' : ''}` }}
                    >
                      <div className={styles.describePainLevelQuestionPainLevelIcon}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="21" viewBox="0 0 20 21" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M10 0.814453C15.5228 0.814453 20 5.29161 20 10.8145C20 16.3373 15.5228 20.8145 10 20.8145C4.47715 20.8145 0 16.3373 0 10.8145C0 5.29161 4.47715 0.814453 10 0.814453ZM10 11.4727C7.84084 11.4728 5.81745 12.8169 5.12695 15.082C4.97664 15.5773 5.61514 15.9332 6.06445 15.6758C6.98835 15.1458 8.39148 14.5441 10 14.5439C11.6349 14.5439 13.0584 15.1651 13.9814 15.7012C14.4174 15.9541 15.0374 15.6144 14.8994 15.1299C14.2435 12.8341 12.1593 11.4727 10 11.4727ZM5.27734 9.24805C5.15161 9.24964 4.55469 10.151 4.55469 10.5498C4.55499 10.9486 4.87852 11.2723 5.27734 11.2725C5.67631 11.2725 5.99969 10.9487 6 10.5498C6 10.1506 5.40235 9.24805 5.27734 9.24805ZM14.7227 9.24805C14.5967 9.25021 14 10.1511 14 10.5498C14.0003 10.9486 14.3239 11.2722 14.7227 11.2725C15.1216 11.2725 15.445 10.9487 15.4453 10.5498C15.4453 10.1506 14.8477 9.24805 14.7227 9.24805ZM7.30957 6.2627C7.07645 6.11492 6.76804 6.18393 6.62012 6.41699C6.47247 6.65007 6.54145 6.95853 6.77441 7.10645L6.9873 7.24219L5.54785 7.62988C5.28143 7.70177 5.12363 7.97566 5.19531 8.24219C5.2671 8.50862 5.54112 8.66623 5.80762 8.59473L8.3457 7.91211C8.53713 7.86061 8.67996 7.70082 8.70996 7.50488C8.73978 7.30889 8.65079 7.11406 8.4834 7.00781L7.30957 6.2627ZM13.3799 6.41699C13.2319 6.18385 12.9226 6.11472 12.6895 6.2627L11.5156 7.00781C11.3485 7.1141 11.2593 7.30907 11.2891 7.50488C11.319 7.70064 11.4622 7.86043 11.6533 7.91211L14.1924 8.59473C14.4589 8.66621 14.7329 8.50858 14.8047 8.24219C14.8764 7.97554 14.7178 7.7016 14.4512 7.62988L13.0117 7.24219L13.2256 7.10645C13.4584 6.95848 13.5275 6.65001 13.3799 6.41699Z"
                            fill={watch('painLevel') === 'worstPainPossible' ? '#fff' : '#C62828'}
                          />
                        </svg>
                      </div>
                      <div
                        className={styles.describePainLevelQuestionPainLevelTitle}
                        style={{ color: `${watch('painLevel') === 'worstPainPossible' ? '#fff' : ''}` }}
                      >
                        <div>Worst Pain</div>
                        <div>Possible</div>
                      </div>
                    </div>
                  </Button>
                </div>
              </div>
              {watch('painLevel') && watch('painLevel') !== 'noPain' && (
                <div className={styles.describePainLevelQuestions}>
                  <div className={styles.describePainLevelQuestion}>
                    <div className={`fs-unmask ${styles.describePainLevelQuestionText}`}>
                      How would you describe the pain? <span>*</span>
                    </div>
                    <MultiSelectInputMUI
                      label=""
                      data-testid="describePain"
                      errors={errors.describePain}
                      name="describePain"
                      onChange={(value: string[]) => {
                        setValue('describePain', value, { shouldValidate: true });
                      }}
                      value={watch('describePain') || []}
                      options={painOptions}
                      withId
                      wrapperClassName={`fs-unmask ${styles.painOptionsWrapper}`}
                      inputClass="fs-exclude"
                    />
                  </div>
                  <div className={styles.describePainLevelQuestion}>
                    <div className={`fs-unmask ${styles.describePainLevelQuestionText}`}>
                      How long have you had this pain? <span>*</span>
                    </div>
                    <MUISelectInput
                      data-testid="daysInPain"
                      label=""
                      errors={errors.daysInPain}
                      name="daysInPain"
                      onChange={(selectedValue: string) => {
                        setValue('daysInPain', selectedValue, { shouldValidate: true });
                      }}
                      value={watch('daysInPain') || ''}
                      wrapperClassName={`fs-unmask ${styles.painOptionsWrapper}`}
                      options={painDurationOptions}
                      inputClass="fs-exclude"
                    />
                  </div>
                </div>
              )}
              <div className={styles.describePainLevelQuestion}>
                <div className={`fs-unmask ${styles.describePainLevelQuestionText}`}>
                  {watch('experiencingEmergency')
                    ? 'What symptoms are you experiencing?'
                    : 'What’s the reason for your visit?'}{' '}
                  <span>*</span>
                </div>
                <Box className={styles.customChipContainer}>
                  {(watch('experiencingEmergency') ? emergencyChips : nonEmergencyChips).map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      data-testid={`descibe-pain-chip-${option.label}`}
                      onClick={() => toggleChip(option.value)}
                      onDelete={isSelected(option.value) ? () => toggleChip(option.value) : undefined}
                      deleteIcon={isSelected(option.value) ? <CloseIcon style={{ color: '#fff' }} /> : undefined}
                      className={`${styles.chip} ${isSelected(option.value) ? styles.selectedChip : ''} fs-exclude`}
                      variant={isSelected(option.value) ? 'filled' : 'outlined'}
                      clickable
                    />
                  ))}
                </Box>
              </div>
              </>
              )}
              <div className={styles.describePainLevelQuestion}>
                <div className={`fs-unmask ${styles.describePainLevelQuestionText}`}>
                  {isClinicalEnablement
                    ? `Anything else you’d like the dentist to know?${noteRequired ? ' *' : ' (optional)'}`
                    : 'Anything else you’d like the dentist to know? (optional)'}
                </div>
                <TextField
                  value={watch('additionalInfo') || ''}
                  label=""
                  data-testid="descibe-pain-anything-else"
                  multiline
                  minRows={2}
                  maxLength={undefined}
                  errors={errors.additionalInfo}
                  name="additionalInfo"
                  onChange={(val) => setValue('additionalInfo', val.target.value, { shouldValidate: true })}
                  wrapperClassName={`fs-unmask ${styles.painOptionsWrapper}`}
                  inputClass="fs-exclude"
                />
              </div>
              <div className={styles.describePainLevelFooter}>
                <Button
                  variant="text"
                  data-testid="descibe-pain-back"
                  className={`fs-unmask ${styles.describePainLevelFooterBack}`}
                  onClick={() => navigate(-1)}
                >
                  <ArrowBackIosNewIcon />
                  Back
                </Button>
                <Button
                  variant="contained"
                  className={`fs-unmask ${styles.describePainLevelFooterNext}`}
                  type="submit"
                  disabled={loading || !isValid}
                  data-testid="descibe-pain-next"
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
          <div className={styles.patientERSectionFullWidth}>
            <div className={styles.patientERSectionParent}>
              <div
                className={`fs-unmask ${styles.patientERSection} ${expandERSection && styles.patientERSectionExpanded}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setExpandERSection((prev) => !prev)}
              >
                <div className={styles.patientERSectionLeft}>
                  <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g id="Icon Right">
                      <path
                        id="Vector"
                        d="M21.2903 14.4996L18.0003 12.5996L21.2903 10.6996C21.7703 10.4196 21.9303 9.80961 21.6603 9.32961L19.6603 5.86961C19.3803 5.38961 18.7703 5.22961 18.2903 5.49961L15.0003 7.39961V3.59961C15.0003 3.04961 14.5503 2.59961 14.0003 2.59961H10.0003C9.4503 2.59961 9.0003 3.04961 9.0003 3.59961V7.39961L5.7103 5.49961C5.2303 5.22961 4.6203 5.38961 4.3403 5.86961L2.3403 9.32961C2.0603 9.80961 2.2303 10.4196 2.7103 10.6996L6.0003 12.5996L2.7103 14.4996C2.2303 14.7796 2.0703 15.3896 2.3403 15.8696L4.3403 19.3296C4.6203 19.8096 5.2303 19.9696 5.7103 19.6996L9.0003 17.7996V21.5996C9.0003 22.1496 9.4503 22.5996 10.0003 22.5996H14.0003C14.5503 22.5996 15.0003 22.1496 15.0003 21.5996V17.7996L18.2903 19.6996C18.7703 19.9796 19.3803 19.8096 19.6603 19.3296L21.6603 15.8696C21.9403 15.3896 21.7703 14.7796 21.2903 14.4996ZM18.4303 17.4696L13.7503 14.7696C13.4203 14.5696 13.0003 14.8096 13.0003 15.1996V20.5996H11.0003V15.1996C11.0003 14.8196 10.5803 14.5696 10.2503 14.7696L5.5703 17.4696L4.5703 15.7396L9.2503 13.0396C9.5803 12.8496 9.5803 12.3696 9.2503 12.1696L4.5703 9.46961L5.5703 7.73961L10.2503 10.4396C10.5803 10.6296 11.0003 10.3896 11.0003 9.99961V4.59961H13.0003V9.99961C13.0003 10.3796 13.4203 10.6296 13.7503 10.4296L18.4303 7.72961L19.4303 9.45961L14.7503 12.1596C14.4203 12.3496 14.4203 12.8296 14.7503 13.0296L19.4303 15.7296L18.4303 17.4696Z"
                        fill="white"
                      />
                    </g>
                  </svg>
                  <span className="fs-unmask">Should I go to the emergency room?</span>
                </div>
                <div className={`fs-unmask ${styles.patientERSectionRight}`}>
                  {!expandERSection ? (
                    <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g id="Icon Right">
                        <path
                          id="Vector"
                          d="M15.0011 8.71499C15.3899 8.3253 15.3896 7.69427 15.0003 7.30499C14.6107 6.91542 13.9791 6.91542 13.5895 7.30499L9.00203 11.8925C8.6115 12.283 8.6115 12.9162 9.00203 13.3067L13.5895 17.8942C13.9791 18.2838 14.6107 18.2838 15.0003 17.8942C15.3896 17.505 15.3899 16.8739 15.0011 16.4842L11.1249 12.5996L15.0011 8.71499Z"
                          fill="white"
                        />
                      </g>
                    </svg>
                  ) : (
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g id="Icon Right">
                        <path
                          id="Vector"
                          d="M8.11538 8.99892C7.72569 8.61008 7.09466 8.61042 6.70538 8.99969C6.31581 9.38927 6.31581 10.0209 6.70538 10.4105L11.2929 14.998C11.6834 15.3885 12.3166 15.3885 12.7071 14.998L17.2946 10.4105C17.6842 10.0209 17.6842 9.38927 17.2946 8.99969C16.9053 8.61042 16.2743 8.61008 15.8846 8.99892L12 12.8751L8.11538 8.99892Z"
                          fill="white"
                        />
                      </g>
                    </svg>
                  )}
                </div>
              </div>
              {expandERSection && (
                <div className={`fs-unmask ${styles.patientERSectionText}`}>
                  <p className={styles.patientERSectionP} ref={erSectionRef}>
                    Although dental emergencies are rare, there are a few life-threatening situations that require
                    immediate supervision. If you experience any of the below, please call 911 or go to your nearest
                    emergency department:
                  </p>
                  <ul className={styles.patientERList}>
                    <li className={styles.patientERSectionP}>• Difficulty breathing</li>
                    <li className={styles.patientERSectionP}>
                      • Intense pain in your left arm that is spreading to your neck and/or jaw
                    </li>
                    <li className={styles.patientERSectionP}>• Intense pain behind the eyes and/or double vision</li>
                    <li className={styles.patientERSectionP}>
                      • Swelling/pressure that is preventing you from swallowing your spit
                    </li>
                    <li className={styles.patientERSectionP}>
                      • Swelling/pressure behind the tongue moving it out of place
                    </li>
                    <li className={styles.patientERSectionP}>
                      • Swelling/pressure from your mouth preventing you from opening your mouth
                    </li>
                    <li className={styles.patientERSectionP}>• Swelling from your mouth to your upper eyelid</li>
                    <li className={styles.patientERSectionP}>
                      • Fever or vomiting possibly caused by a dental infection
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
      <GenericSnackbar open={snackbar.isOpen} message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} />
    </Container>
  );
};
export default DescribePainLevel;
